import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Search,
  Trash2,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  Truck,
  MapPin,
  Filter,
  CheckCircle,
  XCircle,
  MinusCircle,
  AlertTriangle,
  Loader2,
  Droplets
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ChecklistItem {
  id: string;
  label: string;
  status: 'pass' | 'fail' | 'na';
  fault?: string | null;
}

interface PrestartCheck {
  id: string;
  equipment_type: string;
  equipment_id: string;
  equipment_name: string;
  operator_name: string;
  reading_value: number | null;
  reading_unit: string;
  checklist_items: ChecklistItem[];
  status: string;
  created_at: string;
  updated_at: string;
  site_id: string | null;
  site_name: string | null;
  greasing_confirmed: boolean | null;
}

export const PreStartReportsPage: React.FC = () => {
  const [prestarts, setPrestarts] = useState<PrestartCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState<string>('all');
  const [greasingFilter, setGreasingFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    fetchPrestarts();
  }, []);

  const fetchPrestarts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('prestart_checks')
        .select('*')
        .eq('status', 'complete')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPrestarts(data || []);
    } catch (error) {
      console.error('Error fetching prestart checks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this pre-start report? This action cannot be undone.')) return;

    setDeleting(id);
    try {
      const { error } = await supabase
        .from('prestart_checks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setPrestarts(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting prestart check:', error);
      alert('Failed to delete pre-start report. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getChecklistSummary = (items: ChecklistItem[]) => {
    const passed = items.filter(i => i.status === 'pass').length;
    const failed = items.filter(i => i.status === 'fail').length;
    const na = items.filter(i => i.status === 'na').length;
    return { passed, failed, na, total: items.length };
  };

  const filteredPrestarts = prestarts.filter(p => {
    const matchesSearch =
      p.equipment_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.operator_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.site_name && p.site_name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = equipmentTypeFilter === 'all' || p.equipment_type === equipmentTypeFilter;
    const matchesGreasing =
      greasingFilter === 'all' ||
      (greasingFilter === 'greased' && p.greasing_confirmed === true) ||
      (greasingFilter === 'not_greased' && p.greasing_confirmed === false) ||
      (greasingFilter === 'not_reported' && p.greasing_confirmed === null);
    return matchesSearch && matchesType && matchesGreasing;
  });

  const totalPrestarts = prestarts.length;
  const totalWithFaults = prestarts.filter(p =>
    p.checklist_items.some(i => i.status === 'fail')
  ).length;
  const totalGreased = prestarts.filter(p => p.greasing_confirmed === true).length;
  const totalNotGreased = prestarts.filter(p => p.greasing_confirmed === false).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pre-Start Reports</h1>
          <p className="text-sm text-gray-500 mt-1">View and manage completed pre-start checks</p>
        </div>
      </div>

      {/* Summary Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Completed</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalPrestarts}</p>
            </div>
            <div className="h-11 w-11 bg-green-100 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">With Faults</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{totalWithFaults}</p>
            </div>
            <div className="h-11 w-11 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">All Clear</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{totalPrestarts - totalWithFaults}</p>
            </div>
            <div className="h-11 w-11 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className={`bg-white rounded-xl border p-5 ${totalNotGreased > 0 ? 'border-red-200' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Greasing</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-bold text-green-600">{totalGreased}</p>
                {totalNotGreased > 0 && (
                  <p className="text-sm font-semibold text-red-500">/ {totalNotGreased} not greased</p>
                )}
              </div>
            </div>
            <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${totalNotGreased > 0 ? 'bg-red-100' : 'bg-blue-100'}`}>
              <Droplets className={`h-6 w-6 ${totalNotGreased > 0 ? 'text-red-600' : 'text-blue-600'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by equipment, operator, or site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={equipmentTypeFilter}
              onChange={(e) => setEquipmentTypeFilter(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Types</option>
              <option value="vehicle">Vehicles</option>
              <option value="plant">Plant / Machinery</option>
            </select>
            <select
              value={greasingFilter}
              onChange={(e) => setGreasingFilter(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Greasing</option>
              <option value="greased">Greased</option>
              <option value="not_greased">Not Greased</option>
              <option value="not_reported">Not Reported</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredPrestarts.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <ClipboardCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {searchQuery || equipmentTypeFilter !== 'all'
              ? 'No pre-start reports match your filters.'
              : 'No completed pre-start reports found.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPrestarts.map((prestart) => {
            const summary = getChecklistSummary(prestart.checklist_items);
            const hasFaults = summary.failed > 0;
            const isExpanded = expandedId === prestart.id;

            return (
              <div
                key={prestart.id}
                className={`bg-white rounded-xl border ${hasFaults ? 'border-red-200' : 'border-gray-200'} overflow-hidden transition-shadow hover:shadow-md`}
              >
                {/* Card Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : prestart.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          prestart.equipment_type === 'vehicle'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {prestart.equipment_type === 'vehicle' ? 'Vehicle' : 'Plant'}
                        </span>
                        {hasFaults && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {summary.failed} Fault{summary.failed > 1 ? 's' : ''}
                          </span>
                        )}
                        {prestart.greasing_confirmed === true && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            <Droplets className="h-3 w-3 mr-1" />
                            Greased
                          </span>
                        )}
                        {prestart.greasing_confirmed === false && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            <Droplets className="h-3 w-3 mr-1" />
                            Not Greased
                          </span>
                        )}
                        {prestart.greasing_confirmed === null && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                            <Droplets className="h-3 w-3 mr-1" />
                            Greasing N/R
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{prestart.equipment_name}</h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {prestart.operator_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(prestart.created_at)} at {formatTime(prestart.created_at)}
                        </span>
                        {prestart.site_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {prestart.site_name}
                          </span>
                        )}
                        {prestart.reading_value != null && (
                          <span className="flex items-center gap-1">
                            <Truck className="h-3.5 w-3.5" />
                            {Number(prestart.reading_value).toLocaleString()} {prestart.reading_unit}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Pass/Fail summary pills */}
                      <div className="hidden sm:flex items-center gap-1.5">
                        <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                          <CheckCircle className="h-3 w-3" />{summary.passed}
                        </span>
                        {summary.failed > 0 && (
                          <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded-full">
                            <XCircle className="h-3 w-3" />{summary.failed}
                          </span>
                        )}
                        {summary.na > 0 && (
                          <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            <MinusCircle className="h-3 w-3" />{summary.na}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(prestart.id);
                        }}
                        disabled={deleting === prestart.id}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete pre-start report"
                      >
                        {deleting === prestart.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>

                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Checklist */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4">
                    {/* Greasing Acknowledgment */}
                    <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${
                      prestart.greasing_confirmed === true
                        ? 'bg-green-50 border border-green-200'
                        : prestart.greasing_confirmed === false
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-gray-100 border border-gray-200'
                    }`}>
                      <Droplets className={`h-5 w-5 shrink-0 ${
                        prestart.greasing_confirmed === true
                          ? 'text-green-600'
                          : prestart.greasing_confirmed === false
                          ? 'text-red-600'
                          : 'text-gray-400'
                      }`} />
                      <div>
                        <p className={`text-sm font-semibold ${
                          prestart.greasing_confirmed === true
                            ? 'text-green-800'
                            : prestart.greasing_confirmed === false
                            ? 'text-red-800'
                            : 'text-gray-600'
                        }`}>
                          {prestart.greasing_confirmed === true
                            ? 'Greasing Confirmed'
                            : prestart.greasing_confirmed === false
                            ? 'Greasing Not Completed'
                            : 'Greasing Status Not Reported'}
                        </p>
                        <p className={`text-xs mt-0.5 ${
                          prestart.greasing_confirmed === true
                            ? 'text-green-600'
                            : prestart.greasing_confirmed === false
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}>
                          {prestart.greasing_confirmed === true
                            ? `${prestart.operator_name} confirmed the machine was greased`
                            : prestart.greasing_confirmed === false
                            ? `${prestart.operator_name} did not grease the machine`
                            : 'This pre-start was submitted before greasing tracking was enabled'}
                        </p>
                      </div>
                    </div>

                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Checklist Items</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {prestart.checklist_items.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-lg text-sm ${
                            item.status === 'pass'
                              ? 'bg-green-50'
                              : item.status === 'fail'
                              ? 'bg-red-50'
                              : 'bg-gray-100'
                          }`}
                        >
                          {item.status === 'pass' && <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />}
                          {item.status === 'fail' && <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
                          {item.status === 'na' && <MinusCircle className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />}
                          <div className="min-w-0">
                            <span className={`block ${
                              item.status === 'fail' ? 'text-red-800 font-medium' : 'text-gray-700'
                            }`}>
                              {item.label}
                            </span>
                            {item.fault && (
                              <span className="block text-xs text-red-600 mt-0.5">Fault: {item.fault}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
